import { ApolloLink, HttpLink } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { ApolloClient } from '@apollo/client-integration-tanstack-start'
import { createServerOnlyFn } from '@tanstack/react-start'
import { getRequestHeaders } from '@tanstack/react-start/server'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { disableFragmentWarnings } from 'graphql-tag'
import { createApolloCache } from './apollo-cache'

// Avoid "fragment with name X already exists" when the same fragment is parsed from
// different sources (e.g. original gql template vs. printed/serialized form during
// Apollo's reviveTransportedQueryRef / SSR hydration).
disableFragmentWarnings()

export const requestHeadersFn = createServerOnlyFn(() => {
  const headers = getRequestHeaders()
  const headersObj: Record<string, string> = {}
  headers.forEach((value, key) => {
    headersObj[key] = value
  })
  return headersObj
})

// Create a context link that passes headers from SSR rendering
const authLink = new SetContextLink((prevContext) => {
  // Try to get headers from SSR context if available
  let ssrHeaders: Record<string, string> | null = null
  try {
    ssrHeaders = requestHeadersFn()
  } catch {
    // Not in SSR context, continue without SSR headers
  }

  // Merge SSR headers with any existing headers from operation context
  const mergedHeaders: Record<string, string> = { ...prevContext.headers }

  if (ssrHeaders) {
    // Merge SSR headers into merged headers
    Object.assign(mergedHeaders, ssrHeaders)
  }

  return {
    headers: mergedHeaders,
  }
})

/**
 * New Apollo client + cache per call. TanStack Start invokes `getRouter()` once per HTTP
 * request on the server, so each SSR render gets an isolated cache. The browser creates one
 * client per full load via the same `getRouter()` path.
 */
export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    cache: createApolloCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'cache-first',
      },
    },
    link: ApolloLink.from([
      authLink,
      new HttpLink({
        uri: `${getAppConfig().APP_URL}/api/graphql`,
        credentials: 'include',
      }),
    ]),
  })
}
